import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { ModalNext, ModalHeader, ModalBody, ModalFooter, InlineNotification } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';

import styles from './ModalWithAlerts.module.scss';
import peStyles from './PEModalWithAlerts.module.scss';
import alertStyles from '../../../../modules-react/ml-components-common-react/MLAlertGroupComponent/MLAlertGroupComponent.module.scss';
import messages from './MLModalWithAlerts.messages';

import { Button } from '@jutro/legacy/components';

function MLModalWithAlerts(props) {
    const {
        title,
        alertEntity,
        hasErrors,
        CustomComponent,
        overrideProps,
        onAddAction,
        isOpen,
        onReject,
        viewModelService
    } = props;

    const isGPA = appConfig.portalCode === 'GPA';
    const translator = useTranslator();

    const handleAdd = useCallback(() => {
        onAddAction(onReject);
    }, [onAddAction, onReject]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);


    const getErrorMsg = useCallback(() => {
        switch (alertEntity) {
            case 'underlyingPolicy': 
                return <span className="font-weight-bold">{translator(messages.policiesNeedYourAttention)}</span>
            case 'businessActivity': 
                return (
                    <>
                        <h2>{translator(messages.businessActivitiesNeedYourAttention)}</h2>
                        <span>{translator(messages.clickEditLink)}</span>  
                    </>
                );
            case 'pupVehicle':
                return (
                    <>
                        <h2>{translator(messages.vehiclesNeedYourAttention)}</h2>
                        <span>{translator(messages.clickEditAndFillOutRequiredFields)}</span> 
                    </>
                );
            default:
                return ''
        }
    }, [alertEntity, translator]);

    const getAddBtnLabel = useCallback(() => {
        switch (alertEntity) {
            case 'underlyingPolicy': 
                return translator(messages.addPolicy);
            case 'businessActivity': 
                return translator(messages.addBusinessActivity);
            case 'pupVehicle':
                return translator(messages.addVehicle);
            case 'location':
                return translator(messages.addLocation);
            default:
                return '';
        }
    }, [alertEntity, translator]);

    const getModalHeaderClass = useCallback(() => {
        if (isGPA) {
            return hasErrors ? peStyles['modal-header'] : classNames(peStyles['modal-header'], peStyles['modal-header-without-error']);
        }
        return styles['modal-header'];
    }, [hasErrors, isGPA]);

    const customComponentOverrides = {
        viewModelService,
        ...(!_.has(overrideProps, 'onCancel') && { onCancel: handleClose }),
        ...overrideProps
    };

    return (
        <ModalNext
            className={isGPA ? classNames(peStyles['ml-alert-modal-dialog']) : styles['ml-alert-modal-dialog']}
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
        >
            {hasErrors &&
                <div className={isGPA ? classNames('vertical-spacing-30', peStyles.alertContainer) : 'vertical-spacing-30'}>
                    <div className={alertStyles[`wrapper-error`]}>
                        <InlineNotification
                            id='inlineNotification'
                            key='inlineNotification'
                            isDismissable={isGPA}
                            className={classNames(alertStyles.notificationLineGpa, alertStyles.error)}
                            message={getErrorMsg()}
                            onClosed={isGPA ? handleClose : _.noop}
                            messageProps={{
                                error: ' '
                            }}
                            type='error'
                        />
                    </div>
                </div>
            }
            
            <ModalHeader
                title={title}
                onClose={isGPA ? !hasErrors && handleClose : handleClose}
                contentLayout={{
                    component: 'div',
                    componentProps: {
                        className: getModalHeaderClass()
                    }
                }}
                titleLayout={{
                    component: 'grid',
                    componentProps: {
                        className: isGPA ? peStyles['modal-title'] : styles['modal-title']
                    }
                }}
            />
            <ModalBody 
                autoFocus={false}
                contentLayout={{
                    component: 'div',
                    componentProps: {
                        className: isGPA ? peStyles['modal-body'] : styles['modal-body']
                    }
                }}
            >
                <CustomComponent {...customComponentOverrides} />
            </ModalBody>
            <ModalFooter contentLayout={{
                component: 'div',
                componentProps: {
                    className: isGPA ? peStyles['modal-footer'] : styles['modal-footer']
                }
            }}
            >
                <Button id="addButton" onClick={handleAdd} type={isGPA ? 'outlined' : 'filled'} 
                    className={isGPA ? '' : styles['modal-button']}>
                    {getAddBtnLabel()}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

MLModalWithAlerts.propTypes = {
    title: PropTypes.string.isRequired,
    alertEntity: PropTypes.string,
    hasErrors: PropTypes.bool,
    CustomComponent: PropTypes.func,
    overrideProps: PropTypes.shape({}),
    onAddAction: PropTypes.func,
    isOpen: PropTypes.bool,
    onReject: PropTypes.func
};

MLModalWithAlerts.defaultProps = {
    hasErrors: false
};

export default MLModalWithAlerts;